import Component from '@glimmer/component';
import { fn, array } from '@ember/helper';
import { on } from '@ember/modifier';
import { and, or } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import { HdsDropdown } from '@hashicorp/design-system-components/components';

/**
 *
 * An Actions dropdown tied to the MutationContainer's context and is yielded to its parent.
 * Conditionally renders Interactive components for every implemented mutation method (API, CRUD).
 *
 * ```
 * <Page::Projects::MutationContainer::Actions
 *   @model={{this.resource}}
 *   @listItem={{this.isList}}
 *   @showModalChanged={{this.showModalChanged}}
 *   @setResourceToMutate={{this.setResourceToMutate}}
 *   @mutations={{this.resourceScopedService}}
 * />
 * ```
 *
 * @class PageProjectsMutationContainerActions
 *
 */
export default class PageProjectsMutationContainerActions extends Component {
  /**
   * Resource object
   * @argument model
   * @type {Object}
   */
  /**
   * showModal boolean toggle handler
   * @argument showModalChanged
   * @type {Function}
   */
  /**
   * Closure action used to set the current contextual resource targeted for mutation
   * @argument setResourceToMutate
   * @type {Function}
   */
  /**
   * A resource scoped service that defines and handles all resource type mutation actions
   * @argument mutations
   * @type {Object}
   */
  <template>
    {{#let
      (and
        @service.edit
        (can "update project" allowedPermissions=@allowedPermissions)
      )
      (and
        @service.delete
        (can "delete project" allowedPermissions=@allowedPermissions)
      )
      as |canEdit canDelete|
    }}
      {{#if (or canEdit canDelete)}}
        <HdsDropdown data-test-list-item={{@model.id}} as |dd|>
          {{#if @listItem}}
            <dd.ToggleIcon
              @icon="more-horizontal"
              @hasChevron={{false}}
              @text={{t "components.form.more"}}
              {{on
                "click"
                (fn @setResourceToMutate @model @allowedPermissions)
              }}
              data-test-more-menu-trigger={{@model.id}}
            />
          {{else}}
            <dd.ToggleButton
              @color="primary"
              @hasChevron={{false}}
              @text={{t "components.page.projects.settings.action"}}
              {{on
                "click"
                (fn @setResourceToMutate @model @allowedPermissions)
              }}
              data-test-project-manage
            />
          {{/if}}

          {{#if canEdit}}
            <dd.Interactive
              @icon="edit"
              @models={{array @service.orgId @model.id}}
              @route={{@service.edit.route}}
              @text={{t "components.page.projects.edit.title-short"}}
              id="edit-button-{{@model.id}}"
              data-test-edit-button={{@model.id}}
            />
          {{/if}}

          {{#if (and canEdit canDelete)}}
            <dd.Separator />
          {{/if}}

          {{#if canDelete}}
            <dd.Interactive
              @color="critical"
              @icon="trash"
              @text={{t "components.page.projects.delete.action"}}
              id="modal-dialog-open-button-{{@model.id}}"
              data-test-delete-button={{@model.id}}
              {{on "click" (fn @showModalByType "delete")}}
            />
          {{/if}}
        </HdsDropdown>
      {{/if}}
    {{/let}}
  </template>
}
